import { useMemo } from 'react';
import { type DeepPartial, useForm, type UseFormProps, type UseFormReturn } from 'react-hook-form';
import { metricDataSourceValueColumnOptional } from '@gonfalon/dogfood-flags/dogfood-flags';
import { msg } from '@gonfalon/messages/msg';
import {
  dataSourceSqlQueryTooLongMessage,
  isDataSourceSqlQueryTooLong,
  MAX_DATA_SOURCE_TABLE_LENGTH,
} from '@gonfalon/metrics/constants';
import { zodResolver } from '@hookform/resolvers/zod';
import { mergeWith } from 'lodash';
import { z } from 'zod';

// A value that is missing entirely reads as "Required" in the form, the same as a blank one.
// Any other kind of invalid value falls through to zod's own message.
const requiredError = (issue: { input: unknown }) =>
  issue.input === undefined ? msg('metrics.dataSourceForm.required') : undefined;

// A subset of fields which are independently editable without requiring full form submission
const quickEditFields = z.object({
  name: z
    .string({ error: requiredError })
    .min(1, { error: () => msg('metrics.dataSourceForm.required') })
    .max(128, { error: () => msg('metrics.dataSourceForm.nameTooLong') }),
  maintainerId: z
    .string({ error: requiredError })
    .min(1, { error: () => msg('metrics.dataSourceForm.maintainerRequired') }),
  archived: z.boolean({ error: requiredError }),
  description: z
    .string()
    .max(1024, { error: () => msg('metrics.dataSourceForm.descriptionTooLong') })
    .optional(),
});

// This schema aligns with the POST and PATCH metric data source payloads,
// rather than the full MetricDataSource type used elsewhere in order to prevent
// validating properties which are not user-settable.
export const metricDataSourceObjectSchema = z.object({
  dataSource: z
    .object({
      // Field names align with UI components
      key: z
        .string({ error: requiredError })
        .min(1, { error: () => msg('metrics.dataSourceForm.required') })
        .max(128, { error: () => msg('metrics.dataSourceForm.keyTooLong') })
        .regex(/^[a-zA-Z0-9][a-zA-Z0-9._-]*$/, { error: () => msg('metrics.dataSourceForm.keyPattern') }),
      integrationConfig: z.object(
        {
          key: z
            .string({ error: requiredError })
            .min(1, { error: () => msg('metrics.dataSourceForm.integrationRequired') }),
          environmentKey: z
            .string({ error: requiredError })
            .min(1, { error: () => msg('metrics.dataSourceForm.environmentKeyRequired') }),
          id: z.string().optional(),
          projectKey: z.string({ error: requiredError }),
        },
        { error: requiredError },
      ),
      // `inputMethod` and `dbTable` are client-only. Preview/submit resolve the active field:
      // Query uses `sqlQuery`; Table becomes `SELECT * FROM <dbTable>`. The inactive field stays
      // in the form so tab switches do not drop drafts. Persisting the input method is a follow-up.
      inputMethod: z.enum(['query', 'table']).default('query'),
      sqlQuery: z
        .string()
        // `abort` keeps a too-long query from also being reported as missing by the
        // input-method check below, which only runs when the fields it reads are valid.
        .refine((value) => !isDataSourceSqlQueryTooLong(value), {
          error: dataSourceSqlQueryTooLongMessage,
          abort: true,
        })
        .optional(),
      dbTable: z
        .string()
        // `abort` for the same reason as `sqlQuery` above.
        .max(MAX_DATA_SOURCE_TABLE_LENGTH, {
          error: () => msg('metrics.dataSourceForm.tableNameTooLong', { max: MAX_DATA_SOURCE_TABLE_LENGTH }),
          abort: true,
        })
        .optional(),
      columnMappings: z.object({
        columns: z
          .array(
            z.object({
              name: z.string({ error: requiredError }),
              type: z.string({ error: requiredError }),
              nullable: z.boolean().optional(),
              length: z.number().optional(),
              precision: z.number().optional(),
              scale: z.number().optional(),
            }),
            { error: requiredError },
          )
          .min(1, { error: () => msg('metrics.dataSourceForm.columnsRequired') }),
        timestampColumn: z
          .string({ error: requiredError })
          .min(1, { error: () => msg('metrics.dataSourceForm.timestampColumnRequired') }),
        contexts: z
          .array(
            z.tuple([
              z.string().min(1, { error: () => msg('metrics.dataSourceForm.contextKindRequired') }),
              z.string().min(1, { error: () => msg('metrics.dataSourceForm.contextColumnRequired') }),
            ]),
          )
          .min(1, { error: () => msg('metrics.dataSourceForm.contextMappingRequired') })
          .default([['', '']]),
        // Spelled out so the event key column keeps showing the same message it always has.
        keyColumn: z
          .string({ error: requiredError })
          .min(1, { error: () => msg('metrics.dataSourceForm.keyColumnRequired') }),
        valueColumn: z.string().optional(),
      }),
      tags: z.array(z.string(), { error: requiredError }),
    })
    .merge(quickEditFields),
});

// Require the field for the selected input method. The other field may stay filled and is ignored.
const requireActiveInputField = (data: z.infer<typeof metricDataSourceObjectSchema>, ctx: z.RefinementCtx) => {
  const { sqlQuery, dbTable, inputMethod } = data.dataSource;

  if (inputMethod === 'table') {
    if (!dbTable?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['dataSource', 'dbTable'],
        message: msg('metrics.dataSourceForm.required'),
      });
    }
    return;
  }

  if (!sqlQuery?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['dataSource', 'sqlQuery'],
      message: msg('metrics.dataSourceForm.required'),
    });
  }
};

export const metricDataSourceSchema = metricDataSourceObjectSchema.superRefine(requireActiveInputField);

// MTRX-2050: the flag `metricDataSourceValueColumnOptional` controls whether
// the form requires the Event value column. When the flag is off, an explicit
// check enforces the old behavior; downstream stack already handles a nil
// EventValueColumn for non-numeric metrics.
export const buildMetricDataSourceSchema = ({ valueColumnRequired }: { valueColumnRequired: boolean }) => {
  if (!valueColumnRequired) {
    return metricDataSourceSchema;
  }
  return metricDataSourceSchema.superRefine((data, ctx) => {
    const value = data.dataSource.columnMappings.valueColumn;
    if (!value || value.trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['dataSource', 'columnMappings', 'valueColumn'],
        message: msg('metrics.dataSourceForm.valueColumnRequired'),
      });
    }
  });
};

export type MetricDataSourceEditorFormInput = z.input<typeof metricDataSourceSchema>;
export type MetricDataSourceEditorForm = z.output<typeof metricDataSourceSchema>;
export type MetricDataSourceEditorFormReturn = UseFormReturn<
  MetricDataSourceEditorFormInput,
  unknown,
  MetricDataSourceEditorForm
>;

export function isMetricDataSourceEditorFormValid(formValues: unknown) {
  const schema = buildMetricDataSourceSchema({
    valueColumnRequired: !metricDataSourceValueColumnOptional(),
  });

  return schema.safeParse(formValues).success;
}

const emptyDefaultProps = {};

interface MetricDataSourceFormProps {
  defaultValues?: DeepPartial<MetricDataSourceEditorFormInput>;
  mode?: UseFormProps['mode'];
}

export const useMetricDataSourceForm = (props: MetricDataSourceFormProps = emptyDefaultProps) => {
  const mergedDefaultValues = useMemo(() => {
    const defaultValues = {
      dataSource: {
        archived: false,
        columnMappings: {
          columns: [],
          contexts: [['', '']],
        },
        tags: [],
        inputMethod: 'query' as 'query' | 'table',
        sqlQuery: '',
        dbTable: '',
      },
    };

    const merged = mergeWith(defaultValues, props.defaultValues, (objValue, srcValue) => {
      if (Array.isArray(objValue)) {
        return srcValue;
      }
    });

    if (props.defaultValues?.dataSource?.inputMethod === undefined) {
      merged.dataSource.inputMethod = merged.dataSource.dbTable?.trim() ? 'table' : 'query';
    }

    return merged;
  }, [props.defaultValues]);

  const valueColumnOptional = metricDataSourceValueColumnOptional();
  const schema = useMemo(
    () => buildMetricDataSourceSchema({ valueColumnRequired: !valueColumnOptional }),
    [valueColumnOptional],
  );

  const form = useForm<MetricDataSourceEditorFormInput, unknown, MetricDataSourceEditorForm>({
    defaultValues: mergedDefaultValues,
    resolver: zodResolver(schema),
    mode: props.mode ?? 'onSubmit',
    reValidateMode: 'onChange',
  });

  return form;
};
